import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'
import { IconCircleDashedCheck, IconExclamationCircle } from '@tabler/icons-react'
import { Link, Redirect } from 'react-router-dom'

import Select from '@/components/forms/Select'
import { getHierarchyOrganizationOptions, hasChannelAccess } from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import { fetchAgentsByOrg } from '@/reducers/callSearch/callSearch.actions'
import { DragAndDropFileUploader } from '@/components/forms/formik/file-uploader/DragAndDropFileUploader'
import { fetchUploadEmails } from '@/reducers/emails/emails.actions'
import { setData, clearFiles } from '@/reducers/emails/emails.redux'
import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

const UploadEmailsPage = () => {
  const dispatch = useDispatch()
  const { organizationid, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { loading, data: agentData } = useSelector((state) => state.callSearch)
  const { data: emailData, loading: emailLoading } = useSelector((state) => state.emails)
  const organizationOptions = getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const isBaltoAdmin = organizationid === 1

  const handleOrgSelect = (organization) => {
    setSelectedOrg(organization)
    dispatch(fetchAgentsByOrg(organization))
  }

  useEffect(() => {
    if (!isHierarchyManager) {
      handleOrgSelect(organizationid)
    }
  }, [])

  if (!hasChannelAccess(activeChannels, CHANNEL_TYPE.EMAIL, isBaltoAdmin)) {
    return <Redirect to="/upload-interactions" />
  }

  const validationSchema = Yup.object().shape({
    organization: Yup.string().nullable().required(),
    agent_id: Yup.string().nullable().required(),
    files: Yup.array().nullable().required(),
  })

  const initialValues = {
    organization: organizationid,
    agent_id: null,
    agent_channel: 'left',
    files: [],
  }

  const handleSubmit = (values) => {
    dispatch(fetchUploadEmails(values))
  }
  const isUploadCompleted = emailData.isUploadComplete && !emailLoading.emailUpload
  return (
    <>
      <PageHeader data-testid="upload-emails-header" title="Upload Emails" />

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ errors, ...props }) => {
          return (
            <form onSubmit={props.handleSubmit}>
              <div className="filter-grid">
                {isHierarchyManager && (
                  <FormField>
                    <label>Organization</label>
                    <div data-testid="org-dropdown">
                      <Select
                        options={organizationOptions}
                        label="Organization"
                        value={selectedOrg}
                        onChange={(event) => {
                          props.setFieldValue('organization', event.value)
                          handleOrgSelect(event.value)
                          props.setSubmitting(false)
                        }}
                        placeholder="Select Organization"
                        isClearable={false}
                        fixedWidth
                      />
                    </div>
                  </FormField>
                )}
                <FormField>
                  <label>Assign Emails to an Agent</label>
                  <div data-testid="agent-dropdown">
                    <Select
                      placeholder="Select Agent"
                      label="Agent"
                      options={agentData.agents}
                      loading={loading.agents}
                      value={props.values.agent_id}
                      isClearable={false}
                      onChange={(agent) => {
                        props.setFieldValue('agent_id', agent.value)
                        props.setSubmitting(false)
                      }}
                      fixedWidth
                    />
                  </div>
                </FormField>
              </div>

              <div data-testid="upload-emails-page">
                <DragAndDropFileUploader
                  acceptedFileTypes={{ 'message/rfc822': ['.eml'] }}
                  acceptedFileMessage="Supported format: EML"
                  imageCache=""
                  name="files"
                  maxSize={5242880}
                  maxFiles={50}
                  useIconLayout={false}
                  {...props}
                  multiple
                />
              </div>
              <div
                className="flex-align-center flex-space-between"
                data-testid="submit-button-container"
              >
                <div>
                  {!isEmpty(props.values.files) && !props.isSubmitting && (
                    <div>
                      {pluralize('file', props.values.files.length, true)} ready for upload.
                      Double-check the assigned details above before continuing
                    </div>
                  )}
                  {isUploadCompleted && props.isSubmitting && (
                    <div>
                      {!isEmpty(emailData.emails) && (
                        <div className="status-success flex-align-center">
                          <IconCircleDashedCheck className="icon-svg" />
                          <div className="ml">{` ${emailData.emails.length} of ${props.values.files.length} files uploaded successfully! You can view them now in Call Explorer`}</div>
                        </div>
                      )}
                      {!isEmpty(emailData.errors) && (
                        <div className="status-critical flex-align-center">
                          <IconExclamationCircle className="icon-svg" />
                          <div className="ml">{` ${emailData.errors.length} of ${props.values.files.length} files not uploaded successfully`}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!props.isSubmitting && isEmpty(errors) && (
                  <Button brand type="submit">
                    Start Upload
                  </Button>
                )}
                {props.isSubmitting && !isUploadCompleted && (
                  <Button loading brand>
                    Uploading...
                  </Button>
                )}
                {isUploadCompleted && props.isSubmitting && (
                  <div>
                    <Button
                      onClick={() => {
                        props.resetForm()
                        dispatch(clearFiles())
                        dispatch(setData({ isUploadComplete: false }))
                      }}
                    >
                      Reset
                    </Button>
                    {!isEmpty(emailData.emails) && (
                      <Link
                        className="balto-button brand"
                        target="_blank"
                        to={`/messages/${emailData.emails[0].id}`}
                        data-testid="call-explorer-link"
                      >
                        View
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </form>
          )
        }}
      </Formik>
    </>
  )
}

export default UploadEmailsPage
